import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Check, X, Crown, Sparkles, Flame, BarChart3, Mail, 
  Calendar, Building2, Palette, DollarSign, FileText, Youtube, Zap, ArrowLeft
} from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';
import ProducerLayout from '../../components/producer/ProducerLayout';

const FEATURES = [
  { key: 'venues', icon: Building2, label: 'Venues / planos creados', free: '1 venue', premium: '5 venues' },
  { key: 'styles', icon: Palette, label: 'Estilos visuales', free: 'Básico', premium: '9 estilos (Futurista, Neón, Holográfico, Luxury…)' },
  { key: 'backgrounds', icon: Sparkles, label: 'Fondos & formas de canvas', free: 'Sólo cuadrado básico', premium: '8 fondos + 3 formas (incl. circular)' },
  { key: 'duplicate', icon: Calendar, label: 'Duplicar eventos', free: false, premium: true },
  { key: 'youtube', icon: Youtube, label: 'Video de YouTube en banner del evento', free: false, premium: true, note: 'Habilitado por admin' },
  { key: 'heatmap', icon: Flame, label: 'Mapa térmico de demanda', free: false, premium: true },
  { key: 'pricing', icon: DollarSign, label: 'Sugerencias dinámicas de pricing + audit log', free: false, premium: true },
  { key: 'email', icon: Mail, label: 'Enviar campañas de email a compradores', free: false, premium: 'Hasta 1.000 emails/mes' },
  { key: 'early', icon: Zap, label: 'Adelanto de pagos sobre eventos en curso', free: false, premium: 'Hasta 80% del neto' },
  { key: 'report', icon: FileText, label: 'Reporte post-evento', free: 'Sencillo', premium: 'Profesional con Inteligencia de Demanda' },
  { key: 'fee', icon: BarChart3, label: 'Costo de servicio', free: '10% verificado / 15% no verificado', premium: '10% mensual / 0% anual' },
];

const PlanCard = ({ plan, isCurrent, onSelect, ctaLoading }) => {
  const isPremium = plan.id === 'premium';
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: isPremium ? 0.15 : 0 }}
      className={`relative rounded-3xl border p-6 md:p-8 flex flex-col ${
        isPremium 
          ? 'bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-red-500/10 border-amber-500/50 shadow-2xl shadow-amber-500/10' 
          : 'bg-zinc-900/40 border-zinc-800'
      }`}
      data-testid={`plan-card-${plan.id}`}
    >
      {isPremium && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-500 to-orange-500 text-black text-[10px] font-black uppercase tracking-widest px-4 py-1.5 rounded-full flex items-center gap-1.5">
          <Crown size={12} /> Más popular
        </div>
      )}
      
      <div className="text-center mb-6">
        <div className={`inline-flex items-center justify-center w-12 h-12 rounded-2xl mb-3 ${isPremium ? 'bg-amber-500/20' : 'bg-zinc-800'}`}>
          {isPremium ? <Crown size={24} className="text-amber-400" /> : <Building2 size={24} className="text-zinc-400" />}
        </div>
        <h3 className={`text-2xl font-black mb-1 ${isPremium ? 'text-white' : 'text-zinc-200'}`}>{plan.name}</h3>
        <p className="text-xs text-zinc-500 uppercase tracking-wider">{plan.tagline}</p>
      </div>
      
      <div className="text-center mb-6">
        <div className="flex items-baseline justify-center gap-2">
          <span className={`text-5xl font-black ${isPremium ? 'text-white' : 'text-zinc-300'}`}>{plan.price}</span>
          {plan.priceUnit && <span className="text-sm text-zinc-500">{plan.priceUnit}</span>}
        </div>
        {plan.priceNote && <p className="text-xs text-zinc-500 mt-1">{plan.priceNote}</p>}
      </div>
      
      <ul className="space-y-3 mb-6 flex-1">
        {FEATURES.map(f => {
          const value = plan.id === 'premium' ? f.premium : f.free;
          const Icon = f.icon;
          const hasFeature = value && value !== false;
          return (
            <li key={f.key} className="flex items-start gap-3" data-testid={`feature-${plan.id}-${f.key}`}>
              <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${
                hasFeature 
                  ? (isPremium ? 'bg-amber-500/20' : 'bg-emerald-500/20')
                  : 'bg-zinc-800'
              }`}>
                {hasFeature ? (
                  <Check size={12} className={isPremium ? 'text-amber-400' : 'text-emerald-400'} />
                ) : (
                  <X size={12} className="text-zinc-600" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm ${hasFeature ? (isPremium ? 'text-white' : 'text-zinc-300') : 'text-zinc-500'}`}>
                  {f.label}
                </p>
                {typeof value === 'string' && (
                  <p className={`text-xs mt-0.5 ${isPremium ? 'text-amber-400/80' : 'text-zinc-400'}`}>{value}</p>
                )}
                {f.note && hasFeature && (
                  <p className="text-[10px] text-zinc-500 italic mt-0.5">{f.note}</p>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      
      {isCurrent ? (
        <button 
          disabled
          className="w-full py-3 px-4 rounded-xl bg-zinc-800 text-zinc-400 font-semibold text-sm cursor-not-allowed"
          data-testid={`current-plan-${plan.id}`}
        >
          Tu plan actual
        </button>
      ) : isPremium ? (
        <button
          onClick={onSelect}
          disabled={ctaLoading}
          className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black font-bold text-sm uppercase tracking-wider transition-all flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="cta-upgrade-premium"
        >
          <Crown size={14} />
          {ctaLoading ? 'Cargando…' : 'Activar Super Productora'}
        </button>
      ) : (
        <Link
          to="/producer"
          className="w-full py-3 px-4 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-200 font-semibold text-sm text-center transition-colors block"
          data-testid="cta-stay-free"
        >
          Seguir con plan gratuito
        </Link>
      )}
    </motion.div>
  );
};

const SubscriptionComparePage = () => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  
  useEffect(() => {
    api.get('/producers/me/subscription')
      .then(r => setSubscription(r.data?.subscription || r.data))
      .catch(() => setSubscription(null))
      .finally(() => setLoading(false));
  }, []);
  
  const isCurrentPremium = !!subscription?.is_premium;
  
  const handleUpgrade = async () => {
    setUpgrading(true);
    try {
      const r = await api.get('/producers/me/subscription/payment-link');
      if (r.data?.payment_url) {
        window.location.href = r.data.payment_url;
      } else {
        toast.error('Link de pago no disponible');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo obtener el link de pago');
    } finally {
      setUpgrading(false);
    }
  };
  
  const plans = [
    {
      id: 'free',
      name: 'Free',
      tagline: 'Para empezar',
      price: '$0',
      priceUnit: '',
      priceNote: 'Sin tarjeta, para siempre'
    },
    {
      id: 'premium',
      name: 'Super Productora',
      tagline: 'Para crecer y profesionalizarte',
      price: 'Premium',
      priceUnit: '/mes',
      priceNote: 'Plan anual disponible con 0% de costo de servicio'
    }
  ];
  
  return (
    <ProducerLayout title="Comparar planes">
      <div className="max-w-6xl mx-auto px-4 py-8" data-testid="subscription-compare-page">
        {/* Back link */}
        <Link to="/producer" className="inline-flex items-center gap-1 text-zinc-500 hover:text-white text-xs uppercase tracking-wider mb-6">
          <ArrowLeft size={14} /> Volver al portal
        </Link>
        
        {/* Hero */}
        <div className="text-center mb-10 md:mb-12">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-semibold uppercase tracking-wider mb-4"
          >
            <Crown size={14} />
            Planes de productora
          </motion.div>
          <h1 className="text-3xl md:text-5xl font-black text-white mb-3">
            Elegí el plan que <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">acelera tu producción</span>
          </h1>
          <p className="text-zinc-400 max-w-2xl mx-auto">
            Empezá gratis o desbloqueá el set completo de herramientas pro: heatmap de demanda, pricing dinámico, campañas de email, adelantos de pago y mucho más.
          </p>
        </div>
        
        {loading ? (
          <div className="text-center py-20 text-zinc-500">Cargando planes…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            <PlanCard
              plan={plans[0]}
              isCurrent={!isCurrentPremium}
              onSelect={() => {}}
            />
            <PlanCard
              plan={plans[1]}
              isCurrent={isCurrentPremium}
              onSelect={handleUpgrade}
              ctaLoading={upgrading}
            />
          </div>
        )}
        
        {/* FAQ-ish reassurance */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4">
            <p className="text-white font-semibold text-sm mb-1">Sin permanencia</p>
            <p className="text-zinc-500 text-xs">Cancelás cuando quieras. Los datos quedan guardados.</p>
          </div>
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4">
            <p className="text-white font-semibold text-sm mb-1">Soporte prioritario</p>
            <p className="text-zinc-500 text-xs">Las Super Productora tienen atención dedicada.</p>
          </div>
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4">
            <p className="text-white font-semibold text-sm mb-1">Plan anual = 0% fee</p>
            <p className="text-zinc-500 text-xs">Si elegís el plan anual, no pagás costo de servicio sobre tus ventas.</p>
          </div>
        </div>
      </div>
    </ProducerLayout>
  );
};

export default SubscriptionComparePage;
